import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';

/**
 * Top navigation for the Vooxy marketing site. Sticky, translucent on scroll,
 * links: Product, Pricing, Contact + "Log In" CTA (top-right).
 *
 * The Log In button opens a small modal asking for the user's subdomain, then
 * redirects to <subdomain>.vooxy.app/login. Falls back to /login on the same
 * host so preview/localhost still works without a subdomain configured.
 */
export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSubdomainModal, setShowSubdomainModal] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const navLink = (to, label) => (
    <Link
      to={to}
      data-testid={`marketing-nav-${label.toLowerCase()}`}
      className={`px-3 py-2 text-sm font-medium transition-colors ${
        loc.pathname === to ? 'text-emerald-300' : 'text-slate-300 hover:text-white'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'bg-[#050B18]/85 backdrop-blur-xl border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="marketing-nav-logo">
            <img
              src="/vooxy-logo.svg"
              alt="Vooxy"
              className="w-9 h-9 shrink-0 drop-shadow-[0_4px_12px_rgba(16,185,129,0.35)] group-hover:drop-shadow-[0_4px_16px_rgba(16,185,129,0.55)] transition-all"
            />
            <span className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vooxy
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLink('/', 'Product')}
            {navLink('/pricing', 'Pricing')}
            {navLink('/contact', 'Contact')}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setShowSubdomainModal(true)}
              data-testid="marketing-nav-login-btn"
              className="px-4 py-2 text-sm font-medium text-slate-200 hover:text-white transition-colors"
            >
              Log In
            </button>
            <Link
              to="/contact"
              data-testid="marketing-nav-cta"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-500 text-[#050B18] hover:bg-emerald-400 transition-colors shadow-lg shadow-emerald-500/25"
            >
              Talk to Sales
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="marketing-nav-mobile-toggle"
            className="md:hidden p-2 text-slate-300 hover:text-white"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-[#050B18]/95 backdrop-blur-xl border-t border-white/5">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLink('/', 'Product')}
              {navLink('/pricing', 'Pricing')}
              {navLink('/contact', 'Contact')}
              <button
                onClick={() => { setShowSubdomainModal(true); setMobileOpen(false); }}
                data-testid="marketing-nav-login-mobile"
                className="mt-2 px-3 py-2 text-left text-sm font-medium text-slate-200"
              >
                Log In
              </button>
              <Link
                to="/contact"
                data-testid="marketing-nav-cta-mobile"
                className="mt-2 px-4 py-2.5 text-center text-sm font-semibold rounded-lg bg-emerald-500 text-[#050B18]"
              >
                Talk to Sales
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* "Log In" info modal — DELIBERATELY NOT a workspace-slug input.
          Any input field here would let visitors probe for real tenant names.
          Instead we tell the user how to reach their real workspace URL. */}
      {showSubdomainModal && (
        <div
          onClick={() => setShowSubdomainModal(false)}
          className="fixed inset-0 z-50 bg-[#050B18]/80 backdrop-blur-sm grid place-items-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0A1628] border border-white/10 rounded-2xl shadow-2xl p-8"
            data-testid="login-info-modal"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Access your workspace
                </h3>
                <p className="text-sm text-slate-400">Sign in via the URL your admin gave you</p>
              </div>
              <button
                onClick={() => setShowSubdomainModal(false)}
                data-testid="login-info-modal-close"
                className="p-1 text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 text-sm text-slate-300 leading-relaxed">
              <p>
                Vooxy workspaces are private. Your login URL was sent to your workspace admin at onboarding — it looks like:
              </p>
              <div className="px-4 py-3 rounded-lg bg-[#050B18] border border-white/10 font-mono text-emerald-300 text-xs break-all">
                vooxy.app/t/<span className="text-slate-500">your-workspace</span>/login
              </div>
              <p className="text-slate-400">
                Lost your URL? Reach out to whoever set up Vooxy for your company, or contact us and we&apos;ll help.
              </p>
            </div>

            <Link
              to="/contact"
              onClick={() => setShowSubdomainModal(false)}
              data-testid="login-info-contact-btn"
              className="mt-6 w-full block text-center px-4 py-3 rounded-lg bg-emerald-500 text-[#050B18] font-semibold hover:bg-emerald-400 transition-colors"
            >
              Contact support
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
